import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { EstoqueEfetivo } from '../../components/EstoqueEfetivo';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { ModalUltimasAlteracoes } from '../../components/ModalUltimasAlteracoes';
import { Star, ExternalLink, MapPin, History, ChevronUp, ChevronDown } from 'lucide-react';

// Localizacoes por Produtos — o 1o recurso do modulo Estoque (Lancamentos).
//
// POR QUE existe: o contador da tela de Localizacoes diz "36" e nao deixa ver QUAIS. A volta era a
// busca da Lista de Produtos, que e multi-campo: digitar "A" casa descricao, marca e categoria junto
// — inutil pros codigos curtos (A, B, K). A pergunta "o que tem nesta prateleira?" precisa de uma
// tela que so saiba responder isso.
//
// A FORMA (duas colunas, lugares na ordem da RONDA) e de proposito: esta tela e a ancestral direta
// da Conferencia da Fase 1b, que e literalmente percorrer prateleira por prateleira nessa ordem.
// Lista plana com filtro seria jogada fora quando o tablet chegar.
//
// SO LEITURA: a edicao de lugar ja tem dono (a Ficha do Produto). Duas portas de escrita pro mesmo
// dado e o que a DA-324 evitou congelando a coluna de texto — nao reabrir aqui.

const ID_SEM_PRINCIPAL = 'sem-principal';

// Estado da tela entre navegacoes (abrir a ficha do produto e voltar), no padrao da ProdutosLista:
// sessionStorage, limpa quando a aba fecha, volta pro default se o JSON corromper.
//
// O LUGAR e a busca sao "filtro" — persistem enquanto a aba viver, igual aos filtros da Lista de
// Produtos. Ja o PRODUTO marcado e consumido 1x na volta: visita nova comeca sem linha marcada.
const KEY_ESTADO = 'estoque.localizacoes.estado';
const KEY_PRODUTO = 'estoque.localizacoes.produto';

interface EstadoSalvo {
  busca: string;
  mostrarInativos: boolean;
  lugarId: number | string | null;
}

function lerEstado(): EstadoSalvo {
  const def: EstadoSalvo = { busca: '', mostrarInativos: false, lugarId: null };
  try {
    const raw = sessionStorage.getItem(KEY_ESTADO);
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<EstadoSalvo>) };
  } catch { /* JSON corrompido: cai no default em vez de derrubar a tela */ }
  return def;
}

interface Lugar {
  id: number;
  codigo: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  /** TODO vinculo, inclusive de produto inativo (e o numero que o Mesclar usa). */
  produtos: number;
  /** So os vendaveis — e o que esta tela conta, pra bater com a lista de produtos ao lado. */
  produtos_ativos: number;
}

interface ProdutoDoLugar {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status: string;
  marca_nome: string | null;
  unidade_sigla: string | null;
  estoque_atual: string | null;
  estoque_efetivo: string | null;
  estoque_minimo: string | null;
  principal: boolean;
  /** Ordem manual dentro do lugar (a ordem da prateleira). So no lugar real. */
  ordem?: number;
  /** So no lugar real: se o produto tem principal marcada em ALGUM lugar. */
  tem_principal?: boolean;
  /** So no lugar real: os OUTROS lugares do produto. */
  outros_lugares?: string | null;
  /** So na fila da faxina: entre quais lugares escolher. */
  lugares?: string | null;
}

/** `numeric` do Postgres chega como string ("3.000"). `parseQtd` viraria 3000 — usar Number(). */
function num(v: string | null): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

type Pendente = { seletor: string; topoAntes: number } | null;

/** Onde a linha estava ANTES do re-render. Chamado no clique da seta, lido no layout effect. */
function marcarTopo(seletor: string): Pendente {
  const el = document.querySelector(seletor);
  return el ? { seletor, topoAntes: el.getBoundingClientRect().top } : null;
}

/**
 * Devolve a linha movida ao mesmo Y da tela. Sobe pelos ancestrais ate achar quem realmente rola
 * (aqui pode ser o `<div>` da coluna da esquerda, nao a janela). `useLayoutEffect`, nao `useEffect`:
 * roda antes da pintura, entao nao ha flash da linha na posicao errada.
 */
function compensarScroll(ref: { current: Pendente }) {
  const pend = ref.current;
  ref.current = null;
  if (!pend) return;
  const linhaEl = document.querySelector(pend.seletor);
  if (!linhaEl) return;
  const delta = linhaEl.getBoundingClientRect().top - pend.topoAntes;
  if (delta === 0) return;
  let container: Element | null = linhaEl.parentElement;
  while (container) {
    const { overflow, overflowY } = getComputedStyle(container);
    if (/auto|scroll/.test(overflow + overflowY) && container.scrollHeight > container.clientHeight) {
      container.scrollTop += delta;
      return;
    }
    container = container.parentElement;
  }
  window.scrollBy(0, delta);
}

export function LocalizacoesProdutos() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const inicial = useRef(lerEstado());
  const [lugares, setLugares] = useState<Lugar[]>([]);
  const [faxina, setFaxina] = useState<ProdutoDoLugar[]>([]);
  const [produtos, setProdutos] = useState<ProdutoDoLugar[]>([]);
  const [carregandoLugares, setCarregandoLugares] = useState(true);
  const [carregandoProdutos, setCarregandoProdutos] = useState(false);
  const [busca, setBusca] = useState(inicial.current.busca);
  const [mostrarInativos, setMostrarInativos] = useState(inicial.current.mostrarInativos);
  // Qual das duas grades manda nas setas: as duas ouvindo ↑/↓ ao mesmo tempo brigariam.
  // A ultima que voce clicou e quem navega.
  const [foco, setFoco] = useState<'lugares' | 'produtos'>('lugares');
  // So grava o estado depois de restaurar — senao o 1o render (selecao ainda nula) sobrescreveria o
  // lugar salvo com null antes de a restauracao rodar.
  const [restaurado, setRestaurado] = useState(false);
  // Produto com o log aberto. Mora no PAI: a recarga de fundo troca a lista por baixo e um estado
  // dentro da linha morreria junto com ela (DA-298).
  const [verLog, setVerLog] = useState<{ id: number; titulo: string } | null>(null);

  async function carregarLugares() {
    setCarregandoLugares(true);
    try {
      const [lista, fila] = await Promise.all([
        api.get<Lugar[]>('/api/localizacoes'),
        api.get<ProdutoDoLugar[]>('/api/localizacoes/sem-principal/produtos', {
          query: { incluir_inativos: mostrarInativos },
        }),
      ]);
      setLugares(lista);
      setFaxina(fila);
    } catch (e: any) { toast.erro(e.message); }
    finally { setCarregandoLugares(false); }
  }
  useEffect(() => { void carregarLugares(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mostrarInativos]);

  // A busca filtra os LUGARES (a coluna da esquerda), nao os produtos. E a diferenca desta tela pra
  // Lista de Produtos: aqui "A" quer dizer a prateleira A, e so isso — busca SO pelo codigo do lugar.
  // NAO casar a descricao: ela e a ZONA (ESTOQUE, NA FRENTE DA LOJA), um rotulo repetido em quase
  // todo lugar. Incluir a descricao fazia "T" casar "ESTOQUE"/"FRENTE" e nao filtrar nada — a tela
  // se contradizia (reintroduzia o multi-campo que ela nasceu pra evitar).
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return lugares;
    return lugares.filter((l) => l.codigo.toLowerCase().includes(q));
  }, [lugares, busca]);

  // A fila da faxina e um lugar VIRTUAL no fim da lista: nao existe no banco, mas e onde 145 produtos
  // estao presos hoje. Sem ela a fila fica invisivel ate alguem tropecar nela.
  const linhas = useMemo(() => {
    const virtual: Lugar = {
      id: -1, codigo: ID_SEM_PRINCIPAL, descricao: null, ordem: 9e9, ativo: true,
      produtos: faxina.length, produtos_ativos: faxina.length,
    };
    // So com a busca limpa (a busca vira um RECORTE dos lugares reais) e so enquanto a fila existir —
    // faxina acabada nao deixa lugar virtual vazio na tela pra sempre.
    return busca.trim() === '' && faxina.length > 0 ? [...filtrados, virtual] : filtrados;
  }, [filtrados, faxina.length, busca]);

  // As setas da ronda somem com busca ativa: a lista vira um RECORTE, e reordenar recorte bagunca
  // as linhas escondidas (a mordida do DA-306). Mesma trava do cadastro de Localizacoes.
  const podeReordenar = busca.trim() === '' && !carregandoLugares;

  const grid = useGridNavegavel<Lugar>({
    itens: linhas,
    getId: (l) => l.id,
    ativo: foco === 'lugares',
    ativarPorEnter: false, // clicar/navegar JA mostra os produtos — nao ha 2a acao a disparar
  });

  const selecionado = linhas.find((l) => l.id === grid.selectedId) ?? null;
  const eFaxina = selecionado?.id === -1;

  const gridProdutos = useGridNavegavel<ProdutoDoLugar>({
    itens: produtos,
    getId: (p) => p.id,
    onAtivar: (p) => abrirFicha(p),
    ativo: foco === 'produtos',
  });

  // O clique tem que fazer duas coisas: marcar a linha (o hook) e dizer quem manda nas setas.
  function linhaLugar(l: Lugar) {
    const p = grid.rowProps(l);
    return { ...p, onClick: () => { setFoco('lugares'); p.onClick(); } };
  }
  function linhaProduto(p: ProdutoDoLugar) {
    const r = gridProdutos.rowProps(p);
    return { ...r, onClick: () => { setFoco('produtos'); r.onClick(); } };
  }

  // Restaura o lugar que estava marcado. So depois da lista carregar, e so se ele ainda existir
  // (lugar mesclado/inativado no meio some, e ai a tela abre sem selecao em vez de vazia e muda).
  useEffect(() => {
    if (carregandoLugares) return;
    const alvo = inicial.current.lugarId;
    inicial.current.lugarId = null;
    if (alvo != null && linhas.some((l) => l.id === alvo)) grid.setSelectedId(alvo);
    setRestaurado(true);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [carregandoLugares]);

  // A faixa "Produtos sem Estrela" ([DA-601]) manda abrir JA na fila da faxina. OBEDECE e CONSOME
  // ([DA-420]), igual a tela de Inconsistencias: o state gruda no history e sobrevive ao F5, entao
  // sem o `state: null` toda atualizacao voltaria pra fila, ignorando o lugar que o operador
  // escolheu a mao.
  //
  // ⚠️ Espera os lugares carregarem: o lugar virtual so entra em `linhas` DEPOIS que a fila chega
  // do servidor. Marcar um id que ainda nao existe seria selecao perdida em silencio — a tela
  // abriria na lista inteira e a faixa pareceria nao ter feito nada.
  useEffect(() => {
    if (carregandoLugares) return;
    const alvo = (location.state as { lugarId?: string } | null)?.lugarId;
    if (alvo !== ID_SEM_PRINCIPAL) return;
    if (linhas.some((l) => l.id === -1)) {
      setFoco('lugares');
      grid.setSelectedId(-1);
    }
    navigate(location.pathname, { replace: true, state: null });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location.key, carregandoLugares]);

  useEffect(() => {
    if (!restaurado) return;
    sessionStorage.setItem(
      KEY_ESTADO,
      JSON.stringify({ busca, mostrarInativos, lugarId: grid.selectedId } satisfies EstadoSalvo),
    );
  }, [restaurado, busca, mostrarInativos, grid.selectedId]);

  // Re-destaca o produto que estava marcado ao abrir a ficha. Consome a chave (1x): visita nova
  // comeca sem linha marcada, igual a ProdutosLista.
  useEffect(() => {
    if (carregandoProdutos || produtos.length === 0) return;
    const raw = sessionStorage.getItem(KEY_PRODUTO);
    if (raw == null) return;
    sessionStorage.removeItem(KEY_PRODUTO);
    const id = Number(raw);
    if (produtos.some((p) => p.id === id)) {
      gridProdutos.setSelectedId(id);
      setFoco('produtos'); // voltou olhando a direita — as setas seguem o olho
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [carregandoProdutos, produtos]);

  useEffect(() => {
    if (!selecionado) { setProdutos([]); return; }
    if (eFaxina) { setProdutos(faxina); return; }
    let cancelado = false;
    setCarregandoProdutos(true);
    void (async () => {
      try {
        const lista = await api.get<ProdutoDoLugar[]>(`/api/localizacoes/${selecionado.id}/produtos`, {
          query: { incluir_inativos: mostrarInativos },
        });
        if (!cancelado) setProdutos(lista);
      } catch (e: any) { if (!cancelado) toast.erro(e.message); }
      finally { if (!cancelado) setCarregandoProdutos(false); }
    })();
    // Trocar de lugar rapido pelas setas dispara N buscas; a flag descarta a resposta atrasada pra
    // nao pintar a prateleira errada (o alvo da tela e sempre a selecao ATUAL).
    return () => { cancelado = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selecionado?.id, mostrarInativos, faxina]);

  function abrirFicha(p: ProdutoDoLugar) {
    sessionStorage.setItem(KEY_PRODUTO, String(p.id));
    navigate(`/produtos/${p.id}`);
  }

  // Guarda o Y da linha antes do re-render pra compensar o scroll depois (mesma peca da tela de
  // cadastro de Localizacoes): mover uma casa nao deve fazer a lista "pular" sob o olho. Duas
  // grades reordenam aqui (lugares na esquerda, produtos na direita), entao a peca virou funcao
  // com o seletor por parametro em vez de ser copiada.
  const pendProdutoRef = useRef<Pendente>(null);
  const pendLugarRef = useRef<Pendente>(null);
  useLayoutEffect(() => { compensarScroll(pendProdutoRef); }, [produtos]);
  useLayoutEffect(() => { compensarScroll(pendLugarRef); }, [lugares]);

  // Reordena a RONDA (a fila dos lugares na esquerda) — mesmo cano do cadastro de Localizacoes:
  // o backend reescreve `ordem` = 1..N com o array inteiro, nunca troca so os dois vizinhos.
  // Otimista: reordena na tela e persiste; erro recarrega a lista.
  async function moverLugar(lugarId: number, delta: number) {
    const ids = filtrados.map((l) => l.id);
    const i = ids.indexOf(lugarId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    const porId = new Map(lugares.map((l) => [l.id, l]));
    pendLugarRef.current = marcarTopo(`tr[data-loc-id="${lugarId}"]`);
    setLugares(ids.map((x) => porId.get(x)!).filter(Boolean));
    try {
      await api.put('/api/localizacoes/reordenar', { ids });
    } catch (e: any) {
      toast.erro(e.message);
      void carregarLugares();
    }
  }

  // Reordena o produto DENTRO do lugar. So no lugar real (a fila da faxina e virtual, alfabetica —
  // nao ha vinculo pra gravar ordem). Otimista: reordena na tela e persiste; erro recarrega o lugar.
  async function mover(produtoId: number, delta: number) {
    if (!selecionado || eFaxina) return;
    const ids = produtos.map((p) => p.id);
    const i = ids.indexOf(produtoId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    const porId = new Map(produtos.map((p) => [p.id, p]));
    pendProdutoRef.current = marcarTopo(`tr[data-prod-id="${produtoId}"]`);
    setProdutos(ids.map((x) => porId.get(x)!).filter(Boolean));
    try {
      await api.put(`/api/localizacoes/${selecionado.id}/produtos/reordenar`, { ids });
    } catch (e: any) {
      toast.erro(e.message);
      try {
        const lista = await api.get<ProdutoDoLugar[]>(`/api/localizacoes/${selecionado.id}/produtos`, {
          query: { incluir_inativos: mostrarInativos },
        });
        setProdutos(lista);
      } catch { /* o toast do erro original ja avisou */ }
    }
  }

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">Localizacoes por Produtos</h2>
        <div className="pagina-acoes">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
            Mostrar produtos inativos
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* ESQUERDA — os lugares, na ordem da ronda do estoquista */}
        <div style={{ width: 300, flexShrink: 0 }}>
          <input
            type="search" placeholder="Buscar lugar..." className="busca"
            value={busca} onChange={(e) => setBusca(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', border: '1px solid var(--input-border)', borderRadius: 4 }}>
            {carregandoLugares ? <p className="muted" style={{ padding: 10 }}>Carregando...</p> : linhas.length === 0 ? (
              <p className="muted" style={{ padding: 10 }}>Nenhum lugar encontrado.</p>
            ) : (
              <table className="tabela" style={{ margin: 0 }}>
                <tbody>
                  {linhas.map((l, idx) => (
                    <tr key={l.id} data-loc-id={l.id} {...linhaLugar(l)}>
                      {/* A ordem da lista E a ordem da RONDA do estoquista — por isso ela se
                          reordena aqui tambem, e nao so no cadastro: e nesta tela que ele confere
                          prateleira por prateleira e descobre que duas estao trocadas. Mesmo cano
                          (PUT /reordenar), entao as duas telas nunca discordam.
                          A celula fica SEMPRE (igual no cadastro) — so o CONTEUDO some com a busca
                          ativa. Escondendo a `<td>` a coluna encolhia e a lista inteira pulava pra
                          esquerda a cada letra digitada. */}
                      <td
                        style={{ padding: '6px 0 6px 6px', width: 22 }}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        {/* A fila da faxina e um lugar VIRTUAL, sempre no fim: nao existe no banco,
                            nao tem `ordem` pra gravar. Fica sem setas, e quem tem a de baixo
                            desligada e o ultimo lugar REAL. */}
                        {podeReordenar && l.id !== -1 && (
                          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                            <button
                              type="button" title="Mover para cima" disabled={idx === 0}
                              onClick={() => void moverLugar(l.id, -1)}
                              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1 }}
                            >
                              <ChevronUp size={13} />
                            </button>
                            <button
                              type="button" title="Mover para baixo" disabled={idx === filtrados.length - 1}
                              onClick={() => void moverLugar(l.id, 1)}
                              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === filtrados.length - 1 ? 'default' : 'pointer', opacity: idx === filtrados.length - 1 ? 0.25 : 1 }}
                            >
                              <ChevronDown size={13} />
                            </button>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {l.id === -1 ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                            <Star size={12} /> Sem principal
                          </span>
                        ) : (
                          <>
                            <span style={{ fontWeight: 600 }}>{l.codigo}</span>
                            {l.descricao && (
                              <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{l.descricao}</span>
                            )}
                          </>
                        )}
                      </td>
                      {/* O contador segue o MESMO filtro da lista ao lado — senao a esquerda diz 36 e a
                          direita mostra 35 (o B02 tem um produto inativo) e a tela se contradiz. */}
                      <td style={{ padding: '6px 10px', textAlign: 'right', width: 46 }}>
                        {mostrarInativos ? l.produtos : l.produtos_ativos}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* DIREITA — quem mora no lugar selecionado */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selecionado ? (
            <p className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
              <MapPin size={15} /> Escolha um lugar a esquerda pra ver o que mora nele.
            </p>
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  {eFaxina ? 'Produtos sem principal marcada' : selecionado.codigo}
                  {!eFaxina && selecionado.descricao && (
                    <span className="muted" style={{ fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                      {selecionado.descricao}
                    </span>
                  )}
                  <span className="muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                    {produtos.length} produto(s)
                  </span>
                </h3>
                {eFaxina && (
                  <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                    Moram em 2+ lugares e ninguem disse qual e a vitrine — por isso mostram <strong>—</strong> na
                    coluna Local. Abra a ficha e marque a estrela: so voce sabe de onde a venda baixa.
                  </p>
                )}
              </div>

              {carregandoProdutos ? <p className="muted">Carregando...</p> : produtos.length === 0 ? (
                <p className="muted">
                  {eFaxina ? 'Nenhum produto na fila — a faxina acabou.' : 'Nenhum produto neste lugar.'}
                </p>
              ) : (
                <table className="tabela">
                  <thead><tr>
                    {/* So no lugar real: reordenar a fila da faxina (virtual, alfabetica) nao teria
                        onde gravar. As setas espelham as do cadastro de Localizacoes. */}
                    {!eFaxina && <th style={{ width: 30 }}></th>}
                    <th style={{ width: 110 }}>Foto</th>
                    <th style={{ width: 66 }}>Cod.</th>
                    <th>Descricao</th>
                    {/* Folgada: produto fracionado imprime "(+N do pai)" inline, igual na Lista de Produtos. */}
                    <th style={{ width: 130, textAlign: 'right' }} title="Estoque efetivo — o mesmo numero da Lista de Produtos">Est. Atual</th>
                    {/* E o minimo do PRODUTO (quando comprar), nao o da prateleira desta linha —
                        por isso "Min. Compra" aqui tambem, senao a tela de lugares mostraria um
                        "Est. Minimo" que nao e o daquele lugar. */}
                    <th style={{ width: 110, textAlign: 'right', whiteSpace: 'nowrap' }} title="Abaixo disso o produto entra na Lista de Compras">Mín. Compra</th>
                    {/* So na fila: e a coluna que diz entre QUAIS prateleiras decidir — sem ela a faxina
                        perde o proprio motivo de existir. No lugar real ela saiu (pedido do Ruan). */}
                    {eFaxina && <th style={{ width: 150 }}>Escolher entre</th>}
                    <th style={{ width: 56, textAlign: 'right' }}>Acoes</th>
                  </tr></thead>
                  <tbody>
                    {produtos.map((p, idx) => {
                      const acoes: AcaoLinha[] = [
                        { label: 'Abrir ficha', icon: ExternalLink, onClick: () => abrirFicha(p) },
                        {
                          label: 'Quem alterou',
                          icon: History,
                          onClick: () => setVerLog({ id: p.id, titulo: `${p.codigo_interno || ''} ${p.descricao}`.trim() }),
                        },
                      ];
                      const outros = eFaxina ? p.lugares : p.outros_lugares;
                      // A estrela so aparece pra quem mora em MAIS DE UM lugar. Produto de lugar unico
                      // e principal por construcao (o LocalizacoesProduto marca sozinho), entao a
                      // estrela em toda linha nao dizia nada — so poluia. Ela existe pra responder
                      // "de qual das prateleiras a venda baixa?", pergunta que so existe havendo 2+.
                      const multiLugar = !!outros;
                      return (
                        <tr key={p.id} data-prod-id={p.id} {...linhaProduto(p)}>
                          {!eFaxina && (
                            <td
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => e.stopPropagation()}
                            >
                              <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                                <button
                                  type="button" title="Mover para cima" disabled={idx === 0}
                                  onClick={() => void mover(p.id, -1)}
                                  style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1 }}
                                >
                                  <ChevronUp size={13} />
                                </button>
                                <button
                                  type="button" title="Mover para baixo" disabled={idx === produtos.length - 1}
                                  onClick={() => void mover(p.id, 1)}
                                  style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === produtos.length - 1 ? 'default' : 'pointer', opacity: idx === produtos.length - 1 ? 0.25 : 1 }}
                                >
                                  <ChevronDown size={13} />
                                </button>
                              </span>
                            </td>
                          )}
                          <td>
                            {p.foto_url
                              ? <img src={p.foto_url} alt="" className="produto-thumb produto-thumb--grande" loading="lazy" />
                              : <div className="produto-thumb-vazio produto-thumb--grande" />}
                          </td>
                          <td><span className="codigo-curto">{p.codigo_interno ?? '—'}</span></td>
                          <td>
                            {/* A estrela repete a marca da Ficha: amarelo `#fde68a` + preto = o positivo
                                do sistema (ver user_daltonismo). Diz de onde a venda baixa. */}
                            {p.principal && multiLugar && (
                              <span
                                title="Este e o lugar principal — a venda baixa daqui"
                                style={{
                                  background: '#fde68a', color: '#000', borderRadius: 3,
                                  padding: '1px 3px', marginRight: 5, lineHeight: 0,
                                  display: 'inline-block', verticalAlign: 'middle',
                                }}
                              >
                                <Star size={10} fill="#000" />
                              </span>
                            )}
                            {/* Multi-lugar e ninguem marcou a vitrine: e um dos 138 da fila, visto de
                                dentro da prateleira. Sem este selo a linha se confundiria com "a
                                principal e a outra" — e a diferenca e o trabalho que falta. */}
                            {!eFaxina && multiLugar && p.tem_principal === false && (
                              <span
                                title="Ninguem marcou qual e a vitrine deste produto — ele mostra — na coluna Local"
                                style={{
                                  border: '1px solid var(--danger)', color: 'var(--danger)',
                                  borderRadius: 3, padding: '0 4px', marginRight: 5,
                                  fontSize: 10, fontWeight: 600, verticalAlign: 'middle',
                                }}
                              >
                                sem principal
                              </span>
                            )}
                            {p.descricao}
                            {p.status !== 'ativo' && (
                              <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(inativo)</span>
                            )}
                          </td>
                          {/* Espelha a Lista de Produtos linha por linha (mesmo componente, mesma flag
                              `repor-flag`): o numero aqui tem que ser o MESMO de la, senao a tela de
                              estoque vira uma 3a verdade sobre quanto tem. */}
                          <td className="cel-num" style={{ textAlign: 'right' }}>
                            {Number(p.estoque_efetivo ?? p.estoque_atual) <= Number(p.estoque_minimo) && (
                              <span className="repor-flag" title="Repor: estoque efetivo no minimo ou abaixo">▲ </span>
                            )}
                            <EstoqueEfetivo proprio={num(p.estoque_atual)} efetivo={num(p.estoque_efetivo)} />
                          </td>
                          <td className="cel-num" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                            {Number(p.estoque_minimo ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                          </td>
                          {eFaxina && <td className="muted" style={{ fontSize: 12 }}>{outros || '—'}</td>}
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <MenuAcoesLinha acoes={acoes} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {verLog && (
        <ModalUltimasAlteracoes
          produtoId={verLog.id}
          titulo={verLog.titulo}
          onFechar={() => setVerLog(null)}
        />
      )}
    </div>
  );
}
